import React from "react";
import Joyride, { CallBackProps, STATUS, Step } from "react-joyride";
import {
  CalendarJoyrideTooltip,
  type JoyrideTooltipProps,
} from "./CalendarJoyrideTooltip";
import { markTourCompleteOnServer, checkAndMarkOnboardingComplete } from "../../../../../utils/onboarding";

interface CalendarOnboardingProps {
  // Config tour
  tourKey: number;
  steps: Step[];
  runTour: boolean;
  stepIndex: number;
  canSkipTour: boolean;
  onTourCallback: (data: CallBackProps) => void;
  // Booking tour
  runBookingTour: boolean;
  bookingStepIndex: number;
  onBookingTourCallback: (data: CallBackProps) => void;
  // Mini-tour: Specific Day
  runSpecificDayTour: boolean;
  specificDayTourStep: number;
  setSpecificDayTourStep: (v: number) => void;
  specificDayTourKey: number;
  setRunSpecificDayTour: (v: boolean) => void;
  // Mini-tour: Block Schedule
  runBlockTour: boolean;
  blockTourStep: number;
  setBlockTourStep: (v: number) => void;
  blockTourKey: number;
  setRunBlockTour: (v: boolean) => void;
  // Mini-tour: Reschedule Type
  runRescheduleTour: boolean;
  rescheduleTourStep: number;
  setRescheduleTourStep: (v: number) => void;
  rescheduleTourKey: number;
  setRescheduleTourKey: (updater: (prev: number) => number) => void;
  setRunRescheduleTour: (v: boolean) => void;
}

const JOYRIDE_LOCALE = {
  back: "Atrás",
  close: "Cerrar",
  last: "Entendido",
  next: "Continuar",
  skip: "Saltar",
};

const JOYRIDE_STYLES = { options: { primaryColor: "#6D57BC", zIndex: 1300 } };

/** Simple tooltip factory for mini-tours (no cross-tour progress counter). */
function makeMiniTooltip(total: number, currentStep: number) {
  return ((props: JoyrideTooltipProps) => {
    const isMobile = window.innerWidth < 1024;
    const current = currentStep + 1;
    const isLast = current >= total;
    const nextLabel = isLast ? "Entendido" : `Continuar (${current} de ${total})`;
    return (
      <div
        {...props.tooltipProps}
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
          width: isMobile ? "calc(100vw - 2rem)" : undefined,
          maxWidth: isMobile ? "none" : 420,
          padding: isMobile ? "1.25rem 1.25rem 1rem 1.25rem" : "1rem 1rem 0.75rem 1rem",
          color: "#111827",
        }}
      >
        <div style={{ marginBottom: "0.75rem", fontSize: isMobile ? "1rem" : undefined }}>{props.step?.content}</div>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            paddingTop: "0.25rem",
          }}
        >
          <button
            {...props.primaryProps}
            className="btn-primary"
            style={{
              height: isMobile ? 44 : 32,
              fontSize: isMobile ? "1rem" : "0.875rem",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: isMobile ? "0 20px" : "0 14px",
              whiteSpace: "nowrap",
              lineHeight: isMobile ? "44px" : "32px",
              width: isMobile ? "100%" : undefined,
              borderRadius: isMobile ? 10 : undefined,
            }}
          >
            {nextLabel}
          </button>
          {props.index > 0 && (
            <button {...props.backProps} className="btn-outline" style={{ height: isMobile ? 36 : 32, width: isMobile ? "100%" : undefined, borderRadius: isMobile ? 10 : undefined }}>
              Atrás
            </button>
          )}
        </div>
      </div>
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as any;
}

export const CalendarOnboarding = ({
  tourKey,
  steps,
  runTour,
  stepIndex,
  canSkipTour,
  onTourCallback,
  runBookingTour,
  bookingStepIndex,
  onBookingTourCallback,
  runSpecificDayTour,
  specificDayTourStep,
  setSpecificDayTourStep,
  specificDayTourKey,
  setRunSpecificDayTour,
  runBlockTour,
  blockTourStep,
  setBlockTourStep,
  blockTourKey,
  setRunBlockTour,
  runRescheduleTour,
  rescheduleTourStep,
  setRescheduleTourStep,
  rescheduleTourKey,
  setRescheduleTourKey,
  setRunRescheduleTour,
}: CalendarOnboardingProps) => {
  // Main tooltip captures tour state via closure to show combined progress
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const MainTooltip = React.useMemo(() => {
    const Tooltip = (props: JoyrideTooltipProps) => (
      <CalendarJoyrideTooltip
        {...props}
        runTour={runTour}
        stepIndex={stepIndex}
        runBookingTour={runBookingTour}
        bookingStepIndex={bookingStepIndex}
        canSkipTour={canSkipTour}
      />
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Tooltip as any;
  }, [runTour, stepIndex, runBookingTour, bookingStepIndex, canSkipTour]);

  const bookingSteps: Step[] = [
    {
      target: "#btn-agendar-sesion",
      content: "Usá 'Agendar sesión' para crear una sesión con nuestros pacientes.",
      disableBeacon: true,
      spotlightClicks: false,
      disableOverlayClose: true,
      spotlightPadding: 6,
    },
    {
      target: "#select-patient-input",
      content:
        "Aquí vas a poder buscar a nuestros pacientes, que ya tengas asignados previamente, y seleccionar para agendar una sesión.",
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 6,
    },
    {
      target: "#select-patient-type-modal-content",
      content:
        "Seleccioná un paciente de la lista y hacé click en Confirmar para continuar.",
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 6,
    },
    {
      target: "#calendar-grid-area",
      content:
        "Hacé click en un horario disponible para agendar la sesión. Podés usar las flechas de arriba para moverte a la semana siguiente.",
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 6,
    },
    {
      target: "#book-session-modal-content",
      content:
        "Verificá los datos y hacé click en \u201CConfirmar\u201D para agendar la sesión de prueba.",
      disableBeacon: true,
      spotlightClicks: true,
      spotlightPadding: 6,
    },
    {
      target: "#calendar-week-arrows",
      content:
        "Podés moverte entre semanas usando estas flechas para elegir horarios en próximas semanas.",
      disableBeacon: true,
      spotlightClicks: false,
      spotlightPadding: 6,
    },
  ];

  const specificDaySteps: Step[] = [
    {
      target: "#specific-day-panel",
      content:
        "Este es el panel de día específico. Acá podés configurar horarios para una fecha puntual, sin modificar tu horario fijo semanal.",
      disableBeacon: true,
      spotlightClicks: false,
      disableOverlayClose: true,
      spotlightPadding: 6,
      placement: "right" as const,
    },
    {
      target: "#specific-day-date-picker",
      content:
        "Seleccioná la fecha para la que querés configurar horarios. Solo podés elegir fechas de hoy en adelante.",
      disableBeacon: true,
      spotlightClicks: false,
      disableOverlayClose: true,
      spotlightPadding: 4,
      placement: "left" as const,
    },
    {
      target: "#specific-day-ranges",
      content:
        "Acá podés agregar y editar los rangos horarios para ese día. Usá el botón '+ Agregar horario' si necesitás más de un rango.",
      disableBeacon: true,
      spotlightClicks: false,
      disableOverlayClose: true,
      spotlightPadding: 4,
      placement: "left" as const,
    },
    {
      target: "#specific-day-footer",
      content:
        "Cuando termines, hacé click en 'Guardar cambios'. Si te equivocaste, podés usar 'Deshacer' para volver al estado anterior.",
      disableBeacon: true,
      spotlightClicks: false,
      disableOverlayClose: true,
      spotlightPadding: 4,
      placement: "left" as const,
    },
  ];

  const blockSteps: Step[] = [
    {
      target: "#block-schedule-panel",
      content:
        "Este es el panel para bloquear espacios. Podés bloquear horarios donde no querés recibir sesiones, ya sea un día completo o un rango específico.",
      disableBeacon: true,
      spotlightClicks: false,
      disableOverlayClose: true,
      spotlightPadding: 6,
      placement: "right" as const,
    },
    {
      target: "#block-mode-tabs",
      content:
        "Elegí el modo de bloqueo: 'Todo el día' para bloquear la fecha completa, o 'Horario específico' para bloquear solo ciertas horas.",
      disableBeacon: true,
      spotlightClicks: false,
      disableOverlayClose: true,
      spotlightPadding: 4,
      placement: "left" as const,
    },
    {
      target: "#block-date-picker",
      content:
        "Seleccioná la fecha que querés bloquear. Tené en cuenta que solo se pueden bloquear fechas con al menos 48 horas de anticipación.",
      disableBeacon: true,
      spotlightClicks: false,
      disableOverlayClose: true,
      spotlightPadding: 4,
      placement: "left" as const,
    },
    {
      target: "#block-reason",
      content:
        "Agregá una razón para recordar por qué bloqueaste ese horario. Esto te ayuda a mantener organizada tu agenda.",
      disableBeacon: true,
      spotlightClicks: false,
      disableOverlayClose: true,
      spotlightPadding: 4,
      placement: "left" as const,
    },
  ];

  const rescheduleSteps: Step[] = [
    {
      target: "#onboarding-reschedule-mock-row",
      content: (
        <div>
          <p style={{ margin: "0 0 0.5rem" }}>
            Al lado de cada rango horario hay un botón que te permite cambiar su tipo:
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", margin: "0.5rem 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "9999px", fontSize: "0.7rem", fontWeight: 700, background: "#f5f3ff", color: "#7c3aed", border: "1px solid #c4b5fd" }}>Est.</span>
              <span style={{ fontSize: "0.85rem" }}><b>Estándar</b> — disponible para nuevos pacientes y reprogramación.</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "9999px", fontSize: "0.7rem", fontWeight: 700, background: "#fffbeb", color: "#b45309", border: "1px solid #fcd34d" }}>Repr.</span>
              <span style={{ fontSize: "0.85rem" }}><b>Reprogramación</b> — solo para reprogramar sesiones existentes, no se ofrece a nuevos pacientes.</span>
            </div>
          </div>
        </div>
      ),
      disableBeacon: true,
      spotlightClicks: false,
      disableOverlayClose: true,
      spotlightPadding: 6,
      placement: "right" as const,
    },
    {
      target: "#calendar-interactive-grid",
      content: (
        <div>
          <p style={{ margin: "0 0 0.5rem" }}>
            Los horarios de color ámbar son exclusivos para reprogramación de sesiones.
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.6rem", margin: "0.4rem 0 0" }}>
            <div style={{ display: "inline-block", padding: "0.4rem 1rem", borderRadius: 6, background: "rgba(180, 83, 9, 0.18)", border: "1px solid rgba(180, 83, 9, 0.35)", flexShrink: 0 }}>
              <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#92400e" }}>09:00 — 12:00</span>
            </div>
            <span style={{ fontSize: "0.78rem", color: "#6b7280" }}>Así se verá en tu calendario</span>
          </div>
        </div>
      ),
      disableBeacon: true,
      spotlightClicks: false,
      disableOverlayClose: true,
      spotlightPadding: 6,
    },
  ];

  return (
    <>
      {/* Config tour */}
      <Joyride
        key={tourKey}
        steps={steps}
        run={runTour}
        stepIndex={stepIndex}
        continuous
        showSkipButton={canSkipTour}
        scrollToFirstStep
        disableOverlayClose
        disableCloseOnEsc
        disableScrolling={false}
        scrollOffset={400}
        hideCloseButton
        styles={JOYRIDE_STYLES}
        locale={JOYRIDE_LOCALE}
        tooltipComponent={MainTooltip}
        callback={onTourCallback}
      />

      {/* Booking tour */}
      <Joyride
        steps={bookingSteps}
        run={runBookingTour}
        stepIndex={bookingStepIndex}
        continuous
        showSkipButton={canSkipTour}
        scrollToFirstStep
        disableOverlayClose
        disableCloseOnEsc
        disableScrolling={false}
        scrollOffset={400}
        hideCloseButton
        styles={JOYRIDE_STYLES}
        locale={JOYRIDE_LOCALE}
        tooltipComponent={MainTooltip}
        callback={onBookingTourCallback}
      />

      {/* Mini-tour: Specific Day Panel */}
      <Joyride
        key={`specific-day-tour-${specificDayTourKey}`}
        steps={specificDaySteps}
        run={runSpecificDayTour}
        stepIndex={specificDayTourStep}
        continuous
        scrollToFirstStep
        disableOverlayClose
        disableCloseOnEsc
        disableScrolling={false}
        hideCloseButton
        styles={JOYRIDE_STYLES}
        locale={JOYRIDE_LOCALE}
        tooltipComponent={makeMiniTooltip(specificDaySteps.length, specificDayTourStep)}
        callback={(data) => {
          const { status, index, type, action } = data;
          if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            setRunSpecificDayTour(false);
            try { localStorage.setItem("psi-specific-day-tour-done", "1"); } catch { /* noop */ }
            markTourCompleteOnServer("psi-specific-day-tour-done");
            checkAndMarkOnboardingComplete();
            return;
          }
          if (type === "step:after" && action === "next") setSpecificDayTourStep(index + 1);
          if (type === "step:after" && action === "prev") setSpecificDayTourStep(index - 1);
        }}
      />

      {/* Mini-tour: Block Schedule Panel */}
      <Joyride
        key={`block-tour-${blockTourKey}`}
        steps={blockSteps}
        run={runBlockTour}
        stepIndex={blockTourStep}
        continuous
        scrollToFirstStep
        disableOverlayClose
        disableCloseOnEsc
        disableScrolling={false}
        hideCloseButton
        styles={JOYRIDE_STYLES}
        locale={JOYRIDE_LOCALE}
        tooltipComponent={makeMiniTooltip(blockSteps.length, blockTourStep)}
        callback={(data) => {
          const { status, index, type, action } = data;
          if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            setRunBlockTour(false);
            try { localStorage.setItem("psi-block-schedule-tour-done", "1"); } catch { /* noop */ }
            markTourCompleteOnServer("psi-block-schedule-tour-done");
            checkAndMarkOnboardingComplete();
            return;
          }
          if (type === "step:after" && action === "next") setBlockTourStep(index + 1);
          if (type === "step:after" && action === "prev") setBlockTourStep(index - 1);
        }}
      />

      {/* Mini-tour: Reschedule Type */}
      <Joyride
        key={`reschedule-type-tour-${rescheduleTourKey}`}
        steps={rescheduleSteps}
        run={runRescheduleTour}
        stepIndex={rescheduleTourStep}
        continuous
        scrollToFirstStep={false}
        disableOverlayClose
        disableCloseOnEsc
        disableScrolling
        hideCloseButton
        styles={JOYRIDE_STYLES}
        locale={JOYRIDE_LOCALE}
        tooltipComponent={makeMiniTooltip(rescheduleSteps.length, rescheduleTourStep)}
        callback={(data) => {
          const { status, index, type, action } = data;
          if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            try { window.dispatchEvent(new CustomEvent("psi-hide-mock-reschedule")); } catch { /* noop */ }
            setRunRescheduleTour(false);
            try { localStorage.setItem("psi-reschedule-type-tour-done", "1"); } catch { /* noop */ }
            markTourCompleteOnServer("psi-reschedule-type-tour-done");
            checkAndMarkOnboardingComplete();
            return;
          }
          try {
            const t = (data as unknown as { type?: string }).type;
            if (t === "target:notFound" && index === 0) {
              window.setTimeout(() => setRescheduleTourKey((prev) => prev + 1), 60);
              return;
            }
          } catch { /* noop */ }
          if (type === "step:after" && action === "next") setRescheduleTourStep(index + 1);
          if (type === "step:after" && action === "prev") setRescheduleTourStep(index - 1);
        }}
      />
    </>
  );
};
